import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Spacer from '@antimatterhq/ui/components/spacer';
import { Text } from '@antimatterhq/ui/components/typography';
import { Badge } from '@antimatterhq/ui/components/badge';
import {
  Pagination,
  PaginationContent,
  PaginationNext,
  PaginationPrevious,
} from '@antimatterhq/ui/components/pagination';
import { formatNumberWithSign } from '@antimatterhq/ui/utils/format';
import {
  SimpleTable,
  SimpleTableHeader,
  SimpleTableRow,
  SimpleTableHead,
  SimpleTableBody,
  SimpleTableSkeletonRows,
  SimpleTableCell,
} from '@antimatterhq/ui/components/simpleTable';
import { usePaginationManager } from '@/hooks/components/usePaginationManager';
import { useGetObservablesEvents } from '@/hooks/dbui/useGetObservableEvents';
import { EmptyTable } from '@/components/table/empty/EmptyTable';
import { BackLink } from '@/components/link/back/BackLink';

export default function ObservableEvents() {
  const { kind, value } = useParams();
  const { data, isPending, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useGetObservablesEvents({ kind, value });
  const [searchParams, setSearchParams] = useSearchParams();
  const [previousPage, setPreviousPage] = useState('');

  const {
    page,
    goToNextPage,
    goToPreviousPage,
    nextPageEnabled,
    previousPageEnabled,
  } = usePaginationManager({
    fetchNextPage,
    hasNextPage,
    pages: data?.pages || [],
    isFetchingNextPage,
  });

  const items = useMemo(() => {
    return page?.items || [];
  }, [page]);

  useEffect(() => {
    const previous = searchParams.get('previousPage');
    if (!previous) {
      return;
    }

    setPreviousPage(previous);
    searchParams.delete('previousPage');
    setSearchParams(searchParams);
  }, [searchParams, setSearchParams]);

  const loading = !items.length && (isPending || isFetchingNextPage);

  const backRoute = useMemo(() => {
    if (previousPage === 'observables') {
      return '/explore/observables';
    }
    return `/explore/observables/${kind}`;
  }, [previousPage, kind]);

  return (
    <>
      <BackLink fallbackRoute={backRoute} />

      <Spacer size="base" />

      <Text size="lg">Events for {value}</Text>

      <Spacer size="base" />

      <SimpleTable className="w-full" fixed>
        <SimpleTableHeader>
          <SimpleTableRow>
            <SimpleTableHead className="w-[150px] font-bold">
              Adjustment type
            </SimpleTableHead>
            <SimpleTableHead className="w-[150px] font-bold">
              Adjustment amount
            </SimpleTableHead>
            <SimpleTableHead className="w-[100px] font-bold">
              Notable ID
            </SimpleTableHead>
            <SimpleTableHead className="w-auto truncate font-bold">
              Summary
            </SimpleTableHead>
            <SimpleTableHead className="w-[150px] font-bold">
              Rule name
            </SimpleTableHead>
          </SimpleTableRow>
        </SimpleTableHeader>
        <SimpleTableBody>
          {loading && <SimpleTableSkeletonRows cols={5} rows={10} />}
          {!loading &&
            items.map((item, index) => (
              <SimpleTableRow
                key={`${item.notable?.id}_${item.notable?.ruleName}_${index}`}
                className="h-[35px]"
              >
                <SimpleTableCell className="w-[150px]">
                  <Badge
                    variant="filled"
                    color={item.from === 'manual' ? 'default' : 'info'}
                  >
                    {item.from}
                  </Badge>
                </SimpleTableCell>
                <SimpleTableCell className="w-[150px]">
                  <Badge variant="filled" color="info">
                    {formatNumberWithSign(item.adjustBy || 0, 1)}
                  </Badge>
                </SimpleTableCell>
                <SimpleTableCell className="w-[100px]">
                  {item.notable?.id || '--'}
                </SimpleTableCell>
                <SimpleTableCell className="w-auto truncate" tooltip>
                  {item.notable?.summary}
                </SimpleTableCell>
                <SimpleTableCell className="w-[150px]">
                  {item.notable?.ruleName}
                </SimpleTableCell>
              </SimpleTableRow>
            ))}
          {!loading && !items.length && (
            <EmptyTable message="No events here" cols={5} />
          )}
        </SimpleTableBody>
      </SimpleTable>

      <Spacer size="base" />

      {!loading && !!items.length && (
        <Pagination>
          <PaginationContent>
            <PaginationPrevious
              disabled={!previousPageEnabled}
              onClick={goToPreviousPage}
            />
            <PaginationNext
              disabled={!nextPageEnabled}
              onClick={goToNextPage}
            />
          </PaginationContent>
        </Pagination>
      )}
    </>
  );
}
